import * as React from 'react';
import { LikeHeart, ThumbUp as ThumbsUpEmpty, } from '@wix/wix-ui-icons-common/on-stage';
import { Heart, ThumbUp as ThumbsUp, } from '@wix/wix-ui-icons-common/on-stage/reactions';
import { ReactionList } from './ReactionList/ReactionList';
import { Popover } from '../Popover';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { ReactionsDataHooks } from './dataHooks';
import { ReactionButton } from './ReactionButton/ReactionButton';
import { KEYS } from '../../common/keyCodes';
import { st, classes } from './Reactions.st.css';
const POPOVER_ANIMATION_TIMEOUT = 200;
const defaultIcons = {
    heart: {
        fill: React.createElement(Heart, null),
        empty: React.createElement(LikeHeart, null),
    },
    thumbsup: {
        fill: React.createElement(ThumbsUp, null),
        empty: React.createElement(ThumbsUpEmpty, null),
    },
};
/** The Reactions component is used to enable users to express themselves by reacting to a post/event etc */
export const Reactions = React.forwardRef(({ defaultReaction, additionalReactions, onReactionSelect, reactionLabel, reactionIcon, className, selectedId, theme = 'light', popoverTriggerAriaLabel, 'data-hook': dataHook, }, ref) => {
    const [popoverVisible, setPopoverVisibility] = React.useState(false);
    const [autoFocusPopover, setAutoFocusPopover] = React.useState(true);
    const [listRef, setListRef] = React.useState(null);
    const buttonRef = React.useRef();
    const { rtl, mobile } = useTPAComponentsContext();
    useHoverVisibility({
        listComponentRef: listRef?.ref,
        onLeaveBounds: () => setPopoverVisibility(false),
    });
    const defaultReactionLabel = reactionLabel(defaultReaction);
    const reactionDefinitions = React.useMemo(() => {
        const defaultReactionDef = {
            id: defaultReaction,
            label: defaultReactionLabel,
            icon: defaultIcons[defaultReaction].fill,
        };
        return additionalReactions.reduce((acc, reactionId) => [
            ...acc,
            {
                id: reactionId,
                label: reactionLabel(reactionId),
                icon: reactionIcon(reactionId),
            },
        ], [defaultReactionDef]);
    }, [defaultReaction, additionalReactions, reactionLabel, reactionIcon]);
    const selectedReaction = React.useMemo(() => {
        if (selectedId) {
            return reactionDefinitions.find((item) => item.id === selectedId);
        }
        return {
            id: defaultReaction,
            label: defaultReactionLabel,
            icon: defaultIcons[defaultReaction].empty,
        };
    }, [reactionDefinitions, defaultReaction, selectedId]);
    // popover content render is delayed, so we store its ref in state to trigger a rerender
    const listRefSetter = (newListRef) => {
        const listMounted = listRef === null && newListRef !== null;
        const listUnmounted = listRef !== null && newListRef === null;
        if (listMounted || listUnmounted) {
            setListRef(newListRef);
        }
    };
    const onPopoverKeyDown = (e) => {
        switch (e.key) {
            // Tabbing out of the popover
            case KEYS.Tab: {
                setPopoverVisibility(false);
                break;
            }
            // requesting to close the popover
            case KEYS.Escape:
                e.preventDefault();
                setPopoverVisibility(false);
                buttonRef.current.focus();
                break;
            case KEYS.Enter:
            case KEYS.Spacebar:
                e.preventDefault();
                break;
            default:
                return;
        }
    };
    const toggleSelection = (id) => {
        onReactionSelect(id);
        if (popoverVisible) {
            setPopoverVisibility(false);
            buttonRef.current.focus();
        }
    };
    const onTriggerPopover = () => {
        setPopoverVisibility(true);
    };
    const onTriggerReaction = () => {
        buttonRef.current.cancelPopover();
        toggleSelection(selectedId ? null : defaultReaction);
    };
    React.useEffect(() => {
        let focusTimer;
        clearTimeout(focusTimer);
        if (popoverVisible && autoFocusPopover && listRef) {
            focusTimer = window.setTimeout(() => listRef.focus(), POPOVER_ANIMATION_TIMEOUT);
        }
    }, [popoverVisible, listRef]);
    React.useImperativeHandle(ref, () => ({
        openPopover: (shouldFocus) => {
            setAutoFocusPopover(shouldFocus);
            setPopoverVisibility(true);
        },
        closePopover: () => setPopoverVisibility(false),
    }));
    return (React.createElement(Popover, { "data-hook": dataHook, className: st(classes.root, { theme }, className), timeout: POPOVER_ANIMATION_TIMEOUT, shown: popoverVisible, onClickOutside: () => setPopoverVisibility(false), moveBy: { x: rtl ? 10 : -10, y: 4 }, moveArrowTo: rtl ? 0 : 12, placement: rtl ? 'top-end' : 'top-start', "data-mobile": mobile, disableClickOutsideWhenClosed: true, flip: true },
        React.createElement(Popover.Element, null,
            React.createElement(ReactionButton, { ref: buttonRef, active: !!selectedId, selectedReaction: selectedReaction, mobile: mobile, expanded: popoverVisible, popoverTriggerAriaLabel: popoverTriggerAriaLabel, onTriggerPopover: onTriggerPopover, onTriggerReaction: onTriggerReaction })),
        React.createElement(Popover.Content, null,
            React.createElement("div", { onKeyDown: onPopoverKeyDown, "data-hook": ReactionsDataHooks.PopoverContent },
                React.createElement(ReactionList, { ref: listRefSetter, onSelect: toggleSelection, items: reactionDefinitions, selectedId: selectedId })))));
});
Reactions.displayName = 'Reactions';
const useHoverVisibility = ({ listComponentRef, onLeaveBounds, }) => {
    const lastRef = React.useRef();
    const handleHover = React.useCallback((event) => {
        const offset = 20;
        const { top, bottom, left, right } = lastRef.current?.getBoundingClientRect();
        if (!(top - offset < event.clientY &&
            bottom + offset > event.clientY &&
            left - offset < event.clientX &&
            right + offset > event.clientX)) {
            onLeaveBounds();
        }
    }, []);
    React.useEffect(() => {
        if (listComponentRef && listComponentRef !== lastRef.current) {
            lastRef.current = listComponentRef;
            document.addEventListener('mouseover', handleHover);
            return () => {
                document.removeEventListener('mouseover', handleHover);
            };
        }
        return undefined;
    });
};
//# sourceMappingURL=Reactions.js.map